import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

// the least a menu keeps from the window's edges
const MARGIN_REM = 0.5;

const remPx = () =>
  parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

// a menu of `menuitem`s (and their checkbox kind), portalled and fixed at
// `at` so no scrolling container clips it. Opened from the keyboard
// (`focusFirst`) the first item takes focus, as a pointer wouldn't want
// anything lit until it moves. It closes on Escape or Tab (asking to refocus
// whatever opened it), and on a pointer outside it and `opener`, a resize or
// a scroll. Placed where it would stick out of the window, it's moved back in.
export const PopupMenu = (
  { at, onClose, opener, focusFirst = false, children }: {
    at: CSSProperties,
    onClose: (refocus: boolean) => void,
    opener?: RefObject<HTMLElement | null>,
    focusFirst?: boolean,
    children: ReactNode,
  },
) => {
  const menu = useRef<HTMLDivElement>(null);
  const closed = useRef(onClose);
  closed.current = onClose;

  // Measured by its layout box, not getBoundingClientRect(): the opening
  // animation scales it down. The shift is `translate`, which leaves that
  // animation's `transform` alone.
  useLayoutEffect(() => {
    const el = menu.current;
    if (!el) return;
    const margin = remPx() * MARGIN_REM;
    const shift = (start: number, size: number, room: number) =>
      Math.max(margin - start, Math.min(0, room - margin - (start + size)));
    const x = shift(el.offsetLeft, el.offsetWidth, innerWidth);
    const y = shift(el.offsetTop, el.offsetHeight, innerHeight);
    if (x || y) el.style.translate = `${x}px ${y}px`;
  }, []);

  useEffect(() => {
    const first = menu.current?.querySelector<HTMLElement>('[role^=menuitem]');
    ((focusFirst && first) || menu.current)?.focus();
    const outside = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        !menu.current?.contains(target) && !opener?.current?.contains(target)
      ) closed.current(false);
    };
    const dismiss = () => closed.current(false);
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault();
      return closed.current(true);
    }
    // a select keeps its arrows
    if (e.target instanceof HTMLSelectElement) return;
    const items = [
      ...menu.current?.querySelectorAll<HTMLElement>('[role^=menuitem]') ?? [],
    ];
    const i = items.indexOf(document.activeElement as HTMLElement);
    const last = items.length - 1;
    const next = e.key === 'ArrowDown'
      ? (i < last ? i + 1 : 0)
      : e.key === 'ArrowUp'
      ? (i > 0 ? i - 1 : last)
      : e.key === 'Home'
      ? 0
      : e.key === 'End'
      ? last
      : null;
    if (next === null) return;
    e.preventDefault();
    items[next]?.focus();
  };

  return createPortal(
    // events bubble through portals, so they'd reach whatever holds the menu
    <div
      ref={menu}
      className="popup-menu"
      role="menu"
      tabIndex={-1}
      style={at}
      onClick={e => e.stopPropagation()}
      onContextMenu={e => e.preventDefault()}
      onKeyDown={onKeyDown}>
      {children}
    </div>,
    document.body,
  );
};
